/**
 * EdgeTrimMenu — pick the trim for one side of a section.
 *
 * Opened by clicking an edge of the selected section. Lists "Default" (what the
 * rules give this side for whatever it meets), every trim the retailer carries
 * in the section's colour, and "No trim". Names come straight from the product
 * table. Choosing one sets (or clears) the side's entry in `section.edges`.
 *
 * The trim selection matrix, when it exists, narrows this list to the trims
 * that make sense for what the edge meets; nothing else here changes.
 */

import React, { useEffect, useRef } from 'react';
import { EdgeSide } from '../../types/sections';
import { DESIGNER_STRINGS as S } from './strings';

export interface EdgeTrimOption {
  /** Trim product acronym, or 'none'. */
  value: string;
  label: string;
}

export interface EdgeTrimMenuProps {
  side: EdgeSide;
  /** Position inside the viewer, in px. */
  x: number;
  y: number;
  /** What the rules give this side — shown on the Default row. */
  defaultLabel: string;
  /** The side's current override; undefined = default. */
  current: string | undefined;
  options: EdgeTrimOption[];
  onChoose: (value: string | undefined) => void;
  onClose: () => void;
}

const AMBER = '#FEB100';
const WIDTH = 250;

export const EdgeTrimMenu: React.FC<EdgeTrimMenuProps> = ({ side, x, y, defaultLabel, current, options, onChoose, onClose }) => {
  const row = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
    padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#fff',
    background: active ? 'rgba(254,177,0,0.18)' : 'transparent',
    border: active ? `1px solid ${AMBER}` : '1px solid transparent',
  });
  // A press anywhere outside the menu dismisses it.
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, [onClose]);

  const dot = (active: boolean) => (
    <span style={{ width: 8, height: 8, borderRadius: 4, flex: '0 0 auto', background: active ? AMBER : 'rgba(255,255,255,0.25)' }} />
  );

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={S.edgeTrimTitle(S.edgeSide[side])}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', zIndex: 8, width: WIDTH,
        // Keep the menu inside the viewer: open left of / above the click when near the far edges.
        left: `clamp(8px, ${x}px, calc(100% - ${WIDTH + 8}px))`,
        top: `clamp(8px, ${y}px, calc(100% - 320px))`,
        maxHeight: 310, overflowY: 'auto',
        background: 'rgba(26, 26, 30, 0.97)', color: '#fff', borderRadius: 10, padding: 8,
        border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 10px 28px rgba(0,0,0,0.45)',
        pointerEvents: 'auto', userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 6px 6px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#a9a9b4' }}>
        {S.edgeTrimTitle(S.edgeSide[side])}
        <button type="button" onClick={onClose} aria-label={S.close} style={{ background: 'none', border: 'none', color: '#a9a9b4', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 2 }}>×</button>
      </div>

      <button type="button" role="menuitemradio" aria-checked={current === undefined} style={row(current === undefined)} onClick={() => onChoose(undefined)}>
        {dot(current === undefined)}
        <span>{S.edgeTrimDefault} <span style={{ color: '#a9a9b4' }}>— {defaultLabel}</span></span>
      </button>

      {options.map((o) => (
        <button key={o.value} type="button" role="menuitemradio" aria-checked={current === o.value} style={row(current === o.value)} onClick={() => onChoose(o.value)}>
          {dot(current === o.value)}
          {o.label}
        </button>
      ))}
    </div>
  );
};

export default EdgeTrimMenu;
